import { useEffect, useRef, useState } from "react";
import { api, newKey, qs } from "../api.js";
import { fmt, fromLocalInput, t, toLocalInput } from "../i18n.js";
import { go, isLead, isManager, useMe } from "../shared.jsx";
import { uploadFile } from "../uploader.js";
import {
  Confirm, Due, Empty, ErrorBox, Field, Loader, Modal, Pager, PriorityTag, StatusPill, Tabs, useAction, useLoad, useToast,
} from "../ui.jsx";
import { TaskRow } from "./dashboard.jsx";

const QUALITY_ITEMS = ["مطابقة نطاق العمل المطلوب", "جودة التركيب والتشطيب", "نجاح الاختبارات والتشغيل",
  "ترتيب الكابلات والتوسيم", "نظافة الموقع والسلامة"];

// ================================================================ list
export function TaskList({ query }) {
  const me = useMe();
  const manager = isManager(me) || isLead(me);
  const [view, setView] = useState(query.overdue ? "overdue" : manager ? "all" : "mine");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [projectId, setProjectId] = useState(query.project_id || "");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  useEffect(() => { const id = setTimeout(() => { setSearch(q); setPage(1); }, 350); return () => clearTimeout(id); }, [q]);
  const projects = useLoad(() => api.get("/projects?page_size=100"), []);
  const filters = {
    mine: view === "mine" ? 1 : undefined, overdue: view === "overdue" ? 1 : undefined,
    review_queue: view === "review" ? 1 : undefined, status: view === "open" ? "new,in_progress,returned,blocked,pending_review" : status,
    priority, project_id: projectId, q: search, page, page_size: 20,
    sort: view === "review" ? "-updated" : "due",
  };
  const state = useLoad(() => api.get("/tasks" + qs(filters)), [JSON.stringify(filters)]);
  const views = [["mine", "مهامي"], ["open", "المفتوحة"], ["overdue", "المتأخرة"]];
  if (manager) views.unshift(["all", "كل المهام"]), views.push(["review", "بانتظار مراجعتي"]);
  return (
    <div className="page">
      <header className="page-head">
        <h1>{manager ? t("tasks") : t("myTasks")}</h1>
        {manager && <button className="btn btn-primary" onClick={() => setCreating(true)}>مهمة جديدة</button>}
      </header>
      <Tabs tabs={views} value={view} onChange={(v) => { setView(v); setPage(1); }} />
      <div className="filters">
        <input type="search" placeholder="بحث بالعنوان أو رقم المهمة (T-1001)" value={q} onChange={(e) => setQ(e.target.value)}
               aria-label="بحث" />
        <select value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(1); }} aria-label="المشروع">
          <option value="">كل المشاريع</option>
          {(projects.data?.items || []).map((p) => <option key={p.id} value={p.id}>{p.ref_code} — {p.name}</option>)}
        </select>
        {view !== "open" && (
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="الحالة">
            <option value="">كل الحالات</option>
            {Object.keys(t("status")).map((s) => <option key={s} value={s}>{t("status." + s)}</option>)}
          </select>
        )}
        <select value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }} aria-label="الأولوية">
          <option value="">كل الأولويات</option>
          {Object.keys(t("priority")).map((s) => <option key={s} value={s}>{t("priority." + s)}</option>)}
        </select>
      </div>
      <Loader state={state} isEmpty={(d) => d.items.length === 0}
              emptyView={<Empty title="لا توجد مهام مطابقة">{manager ? "أنشئ مهمة جديدة أو غيّر الفلاتر." : "ستظهر هنا المهام المسندة إليك."}</Empty>}>
        {(d) => (
          <>
            <div className="list">{d.items.map((x) => (
              <TaskRow key={x.id} task={x} extra={<>
                {manager ? ` — ${x.assignee_name}` : ""}
                {x.checklist_total ? ` — قائمة التحقق ${x.checklist_done}/${x.checklist_total}` : ""}</>} />))}</div>
            <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} />
          </>
        )}
      </Loader>
      {creating && <TaskForm onClose={() => setCreating(false)} defaultProject={projectId}
                             onSaved={(task) => { setCreating(false); go(`/tasks/${task.id}`); }} />}
    </div>
  );
}

// ================================================================ create / edit
export function TaskForm({ task, defaultProject, onClose, onSaved }) {
  const editing = !!task;
  const [projectId, setProjectId] = useState(task?.project_id || defaultProject || "");
  const [f, setF] = useState(() => ({
    title: task?.title || "", description: task?.description || "", instructions: task?.instructions || "",
    phase_id: task?.phase_id || "", team_id: task?.team_id || "", assignee_id: task?.assignee_id || "",
    priority: task?.priority || "normal", planned_start_at: toLocalInput(task?.planned_start_at),
    due_at: toLocalInput(task?.due_at), estimated_hours: task?.estimated_hours ?? "", difficulty: task?.difficulty || 3,
    depends_on_task_id: task?.depends_on_task_id || "",
    doc: task?.doc_requirements || { before_photos: 1, after_photos: 1, report: true, delivery_files: 0 },
    checklist: "", contributors: [], change_reason: "",
  }));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const projects = useLoad(() => api.get("/projects?page_size=100"), []);
  const project = useLoad(() => (projectId ? api.get(`/projects/${projectId}`) : Promise.resolve(null)), [projectId]);
  const people = useLoad(() => (projectId ? api.get("/users" + qs({ status: "active", page_size: 100, team_id: f.team_id }))
    : Promise.resolve({ items: [] })), [projectId, f.team_id]);
  const deps = useLoad(() => (projectId ? api.get("/tasks" + qs({ project_id: projectId, page_size: 100, sort: "-number" }))
    : Promise.resolve({ items: [] })), [projectId]);
  const { busy, error, run } = useAction();
  const key = useRef(newKey());
  const p = project.data;
  const lockedChange = editing && task.locked_at && (
    f.assignee_id !== task.assignee_id || Number(f.difficulty) !== task.difficulty ||
    fromLocalInput(f.due_at) !== new Date(task.due_at).toISOString());

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      title: f.title.trim(), description: f.description || null, instructions: f.instructions || null,
      phase_id: f.phase_id || null, team_id: f.team_id || null, assignee_id: f.assignee_id,
      priority: f.priority, planned_start_at: fromLocalInput(f.planned_start_at), due_at: fromLocalInput(f.due_at),
      estimated_hours: f.estimated_hours === "" ? null : Number(f.estimated_hours), difficulty: Number(f.difficulty),
      depends_on_task_id: f.depends_on_task_id || null,
      doc_requirements: { before_photos: Number(f.doc.before_photos) || 0, after_photos: Number(f.doc.after_photos) || 0,
        report: !!f.doc.report, delivery_files: Number(f.doc.delivery_files) || 0 },
    };
    if (editing) {
      const changed = Object.fromEntries(Object.entries(body).filter(([k, v]) =>
        JSON.stringify(v) !== JSON.stringify(k.endsWith("_at") && task[k] ? new Date(task[k]).toISOString() : task[k] ?? null)));
      if (lockedChange) changed.change_reason = f.change_reason;
      const saved = await run(() => api.patch(`/tasks/${task.id}`, { version: task.version, ...changed }, { key: key.current }), "تم حفظ التعديلات");
      onSaved(saved);
    } else {
      body.project_id = projectId;
      body.contributor_ids = f.contributors;
      body.checklist = f.checklist.split("\n").map((s) => s.trim()).filter(Boolean).map((title) => ({ title }));
      const saved = await run(() => api.post("/tasks", body, { key: key.current }), "تم إنشاء المهمة وإبلاغ الموظف");
      onSaved(saved);
    }
  };
  const leadOnly = p && !p.my_access.manage;
  const teams = p ? p.teams.filter((tm) => !leadOnly || p.my_access.lead_team_ids.includes(tm.id)) : [];
  const assignable = (projects.data?.items || []);
  return (
    <Modal title={editing ? `تعديل ${task.ref}` : "مهمة جديدة"} onClose={onClose} wide>
      <form className="form-grid" onSubmit={(e) => submit(e).catch(() => {})}>
        {!editing && (
          <Field label="المشروع">
            <select required value={projectId} onChange={(e) => { setProjectId(e.target.value); setF((s) => ({ ...s, team_id: "", phase_id: "" })); }}>
              <option value="">اختر المشروع</option>
              {assignable.map((x) => <option key={x.id} value={x.id}>{x.ref_code} — {x.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="عنوان المهمة"><input required minLength={2} value={f.title} onChange={set("title")} /></Field>
        <Field label="الفريق" hint={leadOnly ? "يمكنك التوزيع داخل فريقك فقط" : undefined}>
          <select value={f.team_id} onChange={set("team_id")} required={leadOnly}>
            <option value="">{leadOnly ? "اختر فريقك" : "بدون فريق"}</option>
            {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
        </Field>
        <Field label="المرحلة">
          <select value={f.phase_id} onChange={set("phase_id")}>
            <option value="">بدون مرحلة</option>
            {(p?.phases || []).map((ph) => <option key={ph.id} value={ph.id}>{ph.name}</option>)}
          </select>
        </Field>
        <Field label="الموظف المسؤول">
          <select required value={f.assignee_id} onChange={set("assignee_id")}>
            <option value="">اختر الموظف</option>
            {(people.data?.items || []).map((u) => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` — ${u.job_title}` : ""}</option>)}
          </select>
        </Field>
        {!editing && (
          <Field label="مساعدون (اختياري)" hint="حصص المساهمة تُعتمد لاحقًا من صفحة المهمة">
            <select multiple value={f.contributors} size={3}
                    onChange={(e) => setF((s) => ({ ...s, contributors: [...e.target.selectedOptions].map((o) => o.value) }))}>
              {(people.data?.items || []).filter((u) => u.id !== f.assignee_id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="الأولوية">
          <div className="seg">{Object.keys(t("priority")).map((k) => (
            <button type="button" key={k} className={"seg-btn" + (f.priority === k ? " seg-on" : "")} onClick={() => set("priority")(k)}>
              {t("priority." + k)}</button>))}</div>
        </Field>
        <Field label={`وزن الصعوبة: ${f.difficulty} من 10`}>
          <input type="range" min={1} max={10} value={f.difficulty} onChange={set("difficulty")} />
        </Field>
        <Field label="البداية المتوقعة (توقيت ليبيا)"><input type="datetime-local" value={f.planned_start_at} onChange={set("planned_start_at")} /></Field>
        <Field label="موعد التسليم (توقيت ليبيا)"><input type="datetime-local" required value={f.due_at} onChange={set("due_at")} /></Field>
        <Field label="ساعات العمل المتوقعة"><input type="number" min={0} step="0.5" inputMode="decimal" value={f.estimated_hours} onChange={set("estimated_hours")} /></Field>
        <Field label="تعتمد على مهمة سابقة">
          <select value={f.depends_on_task_id} onChange={set("depends_on_task_id")}>
            <option value="">لا تعتمد</option>
            {(deps.data?.items || []).filter((x) => x.id !== task?.id).map((x) => <option key={x.id} value={x.id}>{x.ref} — {x.title}</option>)}
          </select>
        </Field>
        <fieldset className="field span-2">
          <legend className="field-label">متطلبات التوثيق الإلزامية</legend>
          <div className="doc-req">
            {[["before_photos", "صور قبل"], ["after_photos", "صور بعد"], ["delivery_files", "ملفات تسليم"]].map(([k, l]) => (
              <label key={k}>{l}<input type="number" min={0} max={50} value={f.doc[k]}
                onChange={(e) => setF((s) => ({ ...s, doc: { ...s.doc, [k]: e.target.value } }))} /></label>))}
            <label className="check"><input type="checkbox" checked={!!f.doc.report}
              onChange={(e) => setF((s) => ({ ...s, doc: { ...s.doc, report: e.target.checked } }))} />تقرير عمل</label>
          </div>
        </fieldset>
        <Field label="الوصف والمطلوب" ><textarea rows={3} value={f.description} onChange={set("description")} /></Field>
        <Field label="تعليمات الإدارة"><textarea rows={3} value={f.instructions} onChange={set("instructions")} /></Field>
        {!editing && (
          <Field label="قائمة التحقق" hint="بند في كل سطر">
            <textarea rows={4} value={f.checklist} onChange={set("checklist")} placeholder={"تمديد الكابلات\nتركيب الكاميرات\nضبط التسجيل على NVR"} />
          </Field>
        )}
        {lockedChange && (
          <Field label="سبب تعديل المسؤول / الوزن / الموعد بعد بدء التنفيذ (إلزامي)">
            <textarea rows={2} required value={f.change_reason} onChange={set("change_reason")} />
          </Field>
        )}
        <div className="span-2"><ErrorBox error={error} />
          {error?.code === "version_conflict" && <p className="muted">أغلق النافذة لتحميل آخر نسخة ثم أعد التعديل.</p>}</div>
        <div className="actions span-2">
          <button className="btn btn-primary" disabled={busy || !projectId}>{busy ? "جارٍ الحفظ…" : editing ? "حفظ التعديلات" : "إنشاء المهمة"}</button>
          <button type="button" className="btn" onClick={onClose}>{t("cancel")}</button>
        </div>
      </form>
    </Modal>
  );
}

// ================================================================ detail
const STAGES = [["new", "التكليف"], ["in_progress", "التنفيذ"], ["pending_review", "المراجعة"], ["approved", "الاعتماد"]];

function CableRun({ status }) {
  const order = { new: 0, in_progress: 1, returned: 1, blocked: 1, pending_review: 2, approved: 3, cancelled: -1 };
  const at = order[status];
  return (
    <ol className={"cable" + (status === "cancelled" ? " cable-cut" : "")} aria-label={"مسار المهمة: " + t("status." + status)}>
      {STAGES.map(([k, label], i) => {
        const cls = at > i || status === "approved" ? "done" : at === i ? "now" : "";
        const flag = i === 1 && (status === "returned" || status === "blocked") ? status : "";
        return (
          <li key={k} className={`cable-node ${cls} ${flag}`}>
            <span className="cable-dot" aria-hidden="true" />
            <span className="cable-label">{label}{flag && <small>{t("status." + status)}</small>}</span>
          </li>
        );
      })}
    </ol>
  );
}

const ACTIVITY = {
  created: "أنشأ المهمة", started: "بدأ التنفيذ", submitted: "أرسل للمراجعة", returned: "أعاد المهمة للتعديل",
  approved: "اعتمد المهمة", preliminary_reviewed: "أنهى المراجعة الأولية", cancelled: "ألغى المهمة", reopened: "أعاد فتح المهمة",
  updated: "عدّل بيانات المهمة", checklist_checked: "أنجز بندًا", checklist_unchecked: "ألغى إنجاز بند",
  checklist_items_added: "أضاف بنودًا لقائمة التحقق", checklist_item_removed: "حذف بندًا", report_added: "أضاف تقرير عمل",
  report_updated: "عدّل تقرير عمل", attachment_added: "رفع ملفًا", attachment_removed: "حذف ملفًا", comment_added: "علّق",
  obstacle_reported: "سجّل عائقًا", obstacle_approved: "اعتمد العائق", obstacle_rejected: "رفض العائق", resumed: "سجّل الاستئناف",
  unblocked: "عادت المهمة للتنفيذ", contributors_changed: "عدّل المساهمين",
};

export function TaskDetail({ id }) {
  const state = useLoad(() => api.get(`/tasks/${id}`), [id]);
  const [tab, setTab] = useState("details");
  const [modal, setModal] = useState(null);
  const { run, busy } = useAction();
  const refresh = state.reload;
  const act = (path, body, ok) => run(() => api.post(`/tasks/${id}/${path}`, body), ok).then((d) => state.setData(d), () => {});
  return (
    <div className="page">
      <a className="back" href="#/tasks"> → المهام</a>
      <Loader state={state}>{(task) => {
        const c = task.can;
        const missing = task.documentation.missing;
        return (
          <>
            <header className="task-head">
              <div>
                <div className="ref">{task.ref} · {task.project_ref}</div>
                <h1>{task.title}</h1>
                <div className="chips"><StatusPill status={task.status} /><PriorityTag priority={task.priority} />
                  <span className="muted">الوزن {task.difficulty}/10</span></div>
              </div>
              <CableRun status={task.status} />
              {c.review_note && <p className="muted review-note">{c.review_note}</p>}
            </header>
            <div className="actionbar">
              {c.start && <button className="btn btn-primary" disabled={busy} onClick={() => act("start", {}, "بدأ التنفيذ")}>بدء التنفيذ</button>}
              {c.submit && <button className="btn btn-primary" disabled={busy}
                onClick={() => (missing.length ? setTab("documentation") || setModal("missing") : act("submit", {}, "أُرسلت المهمة للمراجعة"))}>
                إرسال للمراجعة</button>}
              {c.review_final && <button className="btn btn-primary" onClick={() => setModal("review")}>مراجعة واعتماد</button>}
              {c.review_preliminary && <button className="btn" onClick={() => setModal("prelim")}>مراجعة أولية</button>}
              {c.report_obstacle && <button className="btn" onClick={() => setModal("obstacle")}>تسجيل عائق</button>}
              {c.edit && <button className="btn" onClick={() => setModal("edit")}>تعديل</button>}
              {c.edit && <button className="btn" onClick={() => setModal("contributors")}>المساهمون</button>}
              {c.cancel && <button className="btn btn-ghost-danger" onClick={() => setModal("cancel")}>إلغاء المهمة</button>}
              {c.reopen && <button className="btn" onClick={() => setModal("reopen")}>إعادة فتح</button>}
            </div>
            {task.status === "returned" && task.reviews[0]?.decision === "returned" && (
              <div className="callout callout-warn"><strong>سبب الإعادة:</strong> {task.reviews[0].reason}
                <span className="muted"> ({t("rework." + task.reviews[0].rework_cause)} — {task.reviews[0].reviewer_name})</span></div>
            )}
            <Tabs value={tab} onChange={setTab} tabs={[
              ["details", "التفاصيل"],
              ["checklist", "قائمة التحقق", `${task.documentation.checklist.done}/${task.documentation.checklist.total}`],
              ["documentation", "التوثيق", missing.length ? "!" : null],
              ["comments", "التعليقات والمراجعات", task.comment_count || null],
              ["activity", "السجل"],
            ]} />
            {tab === "details" && <Details task={task} onChanged={refresh} />}
            {tab === "checklist" && <Checklist task={task} onChanged={refresh} />}
            {tab === "documentation" && <Documentation task={task} onChanged={refresh} />}
            {tab === "comments" && <Comments task={task} />}
            {tab === "activity" && <Activity id={id} />}

            {modal === "missing" && (
              <Modal title="لا يمكن الإرسال بعد" onClose={() => setModal(null)}>
                <p>أكمل ما يلي ثم أرسل المهمة للمراجعة:</p>
                <ul className="missing">{missing.map((m) => <li key={m.code}>{m.message}</li>)}</ul>
                <div className="actions"><button className="btn btn-primary" onClick={() => setModal(null)}>حسنًا</button></div>
              </Modal>
            )}
            {(modal === "review" || modal === "prelim") && (
              <ReviewModal task={task} preliminary={modal === "prelim"} onClose={() => setModal(null)} onDone={(d) => { state.setData(d); setModal(null); }} />
            )}
            {modal === "obstacle" && <ObstacleModal task={task} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
            {modal === "edit" && <TaskForm task={task} onClose={() => { setModal(null); refresh(); }} onSaved={(d) => { state.setData(d); setModal(null); }} />}
            {modal === "contributors" && <ContributorsModal task={task} onClose={() => setModal(null)} onDone={(d) => { state.setData(d); setModal(null); }} />}
            {modal === "cancel" && <Confirm title="إلغاء المهمة" danger needReason actionLabel="إلغاء المهمة"
              text="المهمة الملغاة لا تدخل في نسبة الإنجاز ولا في التقييم."
              onConfirm={(reason) => api.post(`/tasks/${id}/cancel`, { reason }).then(state.setData)} onClose={() => setModal(null)} />}
            {modal === "reopen" && <Confirm title="إعادة فتح مهمة معتمدة" needReason actionLabel="إعادة الفتح"
              text="تبقى النسخة المعتمدة محفوظة في السجل، وتعود المهمة إلى التنفيذ."
              onConfirm={(reason) => api.post(`/tasks/${id}/reopen`, { reason }).then(state.setData)} onClose={() => setModal(null)} />}
          </>
        );
      }}</Loader>
    </div>
  );
}

function Info({ label, children }) {
  return <div className="info"><dt>{label}</dt><dd>{children || "—"}</dd></div>;
}

function Details({ task, onChanged }) {
  return (
    <div className="grid-2">
      <section className="panel">
        <h2>المطلوب</h2>
        {task.description ? <p className="prose">{task.description}</p> : <p className="muted">لا يوجد وصف.</p>}
        {task.instructions && <><h3>تعليمات الإدارة</h3><p className="prose">{task.instructions}</p></>}
        <dl className="infos">
          <Info label="المشروع"><a href={`#/projects/${task.project_id}`}>{task.project_name}</a></Info>
          <Info label="المرحلة">{task.phase_name}</Info>
          <Info label="الفريق">{task.team_name}</Info>
          <Info label="المسؤول">{task.assignee_name}{task.assignee_share_pct !== 100 ? ` (${task.assignee_share_pct}%)` : ""}</Info>
          <Info label="المساعدون">{task.contributors.map((c) => `${c.name}${Number(c.share_pct) ? ` (${c.share_pct}%)` : ""}`).join("، ")}</Info>
          <Info label="البداية المتوقعة">{fmt.dt(task.planned_start_at)}</Info>
          <Info label="موعد التسليم"><Due task={task} /></Info>
          <Info label="الساعات المتوقعة">{task.estimated_hours}</Info>
          <Info label="تعتمد على">{task.depends_on_task_id && <a href={`#/tasks/${task.depends_on_task_id}`}>T-{task.depends_on_number} {task.depends_on_title} ({t("status." + task.depends_on_status)})</a>}</Info>
          {task.approved_at && <Info label="الاعتماد">{task.approved_by_name} — {fmt.dt(task.approved_at)}</Info>}
        </dl>
      </section>
      <section className="panel">
        <h2>الموقع</h2>
        <p>{task.city}{task.address ? ` — ${task.address}` : ""}</p>
        {task.maps ? (
          <div className="actions">
            <a className="btn btn-primary" href={task.maps.google} target="_blank" rel="noopener">الاتجاهات في خرائط Google</a>
            <a className="btn" href={task.maps.apple} target="_blank" rel="noopener">خرائط Apple</a>
          </div>
        ) : <p className="muted">لم يُحدد موقع المشروع على الخريطة.</p>}
        <Obstacles task={task} onChanged={onChanged} />
      </section>
    </div>
  );
}

function Obstacles({ task, onChanged }) {
  const [deciding, setDeciding] = useState(null);
  if (!task.obstacles.length) return null;
  return (
    <>
      <h3>العوائق</h3>
      <div className="list">{task.obstacles.map((o) => (
        <div className="row row-static" key={o.id}>
          <div className="row-main">
            <div className="row-title">{t("reason." + o.reason_code)} — {o.blocking === "full" ? "يمنع التنفيذ كليًا" : "جزئي"}</div>
            <div className="row-sub">{o.description}</div>
            <div className="row-sub">حدث {fmt.dt(o.occurred_at)} — {o.reporter_name}</div>
            {o.approved_start && <div className="row-sub">الفترة المعتمدة: {fmt.dt(o.approved_start)} ← {o.approved_end ? fmt.dt(o.approved_end) : "مستمرة"}</div>}
            {o.decision_note && <div className="row-sub">القرار: {o.decision_note}</div>}
          </div>
          <div className="row-side">
            <span className={"pill pill-ob-" + o.status}>{t("obstacleStatus." + o.status)}</span>
            {task.can.decide_obstacles && o.status === "pending" && <button className="btn btn-sm" onClick={() => setDeciding({ o, mode: "decide" })}>اتخاذ قرار</button>}
            {task.can.decide_obstacles && o.status === "approved" && !o.resumed_at && <button className="btn btn-sm" onClick={() => setDeciding({ o, mode: "resume" })}>تسجيل الاستئناف</button>}
          </div>
        </div>))}</div>
      {deciding && <ObstacleDecision {...deciding} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); onChanged(); }} />}
    </>
  );
}

export function ObstacleDecision({ o, mode, onClose, onDone }) {
  const [decision, setDecision] = useState("approved");
  const [note, setNote] = useState("");
  const [start, setStart] = useState(toLocalInput(o.occurred_at));
  const [end, setEnd] = useState("");
  const { busy, error, run } = useAction();
  const submit = () => run(() => (mode === "resume"
    ? api.post(`/obstacles/${o.id}/resume`, { resumed_at: end ? fromLocalInput(end) : null, note: note || null })
    : api.post(`/obstacles/${o.id}/decision`, {
      decision, note: note || null,
      approved_start: decision === "approved" ? fromLocalInput(start) : null,
      approved_end: decision === "approved" && end ? fromLocalInput(end) : null,
    })), "تم حفظ القرار").then(onDone, () => {});
  return (
    <Modal title={mode === "resume" ? "تسجيل استئناف العمل" : "قرار بشأن العائق"} onClose={onClose}>
      <p className="muted">{t("reason." + o.reason_code)}: {o.description}</p>
      {mode === "decide" && (
        <div className="seg">
          <button className={"seg-btn" + (decision === "approved" ? " seg-on" : "")} onClick={() => setDecision("approved")}>اعتماد</button>
          <button className={"seg-btn" + (decision === "rejected" ? " seg-on" : "")} onClick={() => setDecision("rejected")}>رفض</button>
        </div>
      )}
      {mode === "decide" && decision === "approved" && (
        <>
          <Field label="بداية فترة التعطّل المعتمدة"><input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="نهاية الفترة (اتركها فارغة إذا ما زال العائق قائمًا)"><input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          <p className="muted small">تُستبعد هذه الفترة فقط من حساب التأخير. الموعد الأصلي يبقى محفوظًا.</p>
        </>
      )}
      {mode === "resume" && <Field label="وقت الاستئناف (فارغ = الآن)"><input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>}
      <Field label={decision === "rejected" && mode === "decide" ? "سبب الرفض (إلزامي)" : "ملاحظة"}>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <ErrorBox error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? "جارٍ الحفظ…" : "حفظ القرار"}</button>
        <button className="btn" onClick={onClose}>{t("cancel")}</button>
      </div>
    </Modal>
  );
}

function Checklist({ task, onChanged }) {
  const [items, setItems] = useState(task.checklist);
  const [adding, setAdding] = useState("");
  const { run } = useAction();
  useEffect(() => setItems(task.checklist), [task]);
  const toggle = (it) => run(() => api.patch(`/checklist-items/${it.id}`, { done: !it.done }))
    .then((saved) => setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, ...saved } : x))), () => {});
  const add = () => run(() => api.post(`/tasks/${task.id}/checklist`, { items: adding.split("\n").filter((s) => s.trim()).map((title) => ({ title: title.trim() })) }), "أُضيفت البنود")
    .then(() => { setAdding(""); onChanged(); }, () => {});
  return (
    <section className="panel">
      {items.length === 0 && <p className="muted">لا توجد بنود في قائمة التحقق.</p>}
      <ul className="checklist">{items.map((it) => (
        <li key={it.id} className={it.done ? "done" : ""}>
          <label>
            <input type="checkbox" checked={it.done} disabled={!task.can.work} onChange={() => toggle(it)} />
            <span>{it.title}{!it.required && <small> (اختياري)</small>}</span>
          </label>
          {it.done && <small className="muted">{it.done_by_name} — {fmt.dt(it.done_at)}</small>}
          {task.can.edit && !it.done && (
            <button className="icon-btn" aria-label="حذف البند" onClick={() => run(() => api.del(`/checklist-items/${it.id}`)).then(onChanged, () => {})}>✕</button>
          )}
        </li>))}</ul>
      {!task.can.work && task.status === "new" && <p className="muted small">ابدأ التنفيذ لتتمكن من تحديث القائمة.</p>}
      {task.can.edit && (
        <div className="stack">
          <Field label="إضافة بنود (بند في كل سطر)"><textarea rows={2} value={adding} onChange={(e) => setAdding(e.target.value)} /></Field>
          <button className="btn" disabled={!adding.trim()} onClick={add}>إضافة</button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- documentation
function Documentation({ task, onChanged }) {
  const d = task.documentation;
  const req = d.requirements;
  const groups = ["before", "after", "delivery", "report", "obstacle_evidence", "instruction", "other"];
  const byCat = (c) => task.attachments.filter((a) => a.category === c);
  const reqRows = [["before_photos", "صور قبل التنفيذ", d.have.before_photos], ["after_photos", "صور بعد التنفيذ", d.have.after_photos],
    ["delivery_files", "ملفات التسليم", d.have.delivery_files]].filter(([k]) => req[k]);
  return (
    <>
      <section className="panel">
        <h2>المتطلبات</h2>
        {reqRows.length === 0 && !req.report ? <p className="muted">لا توجد متطلبات توثيق إلزامية لهذه المهمة.</p> : (
          <ul className="reqs">
            {reqRows.map(([k, l, have]) => <li key={k} className={have >= req[k] ? "ok" : ""}>{l}: {have} من {req[k]}</li>)}
            {req.report && <li className={d.have.reports ? "ok" : ""}>تقرير العمل: {d.have.reports ? "مضاف" : "مطلوب"}</li>}
            <li className={d.checklist.required_done >= d.checklist.required ? "ok" : ""}>قائمة التحقق: {d.checklist.required_done} من {d.checklist.required}</li>
          </ul>
        )}
        {task.can.add_documentation && <Uploader task={task} onUploaded={onChanged} />}
      </section>
      <Reports task={task} onChanged={onChanged} />
      {groups.map((g) => byCat(g).length > 0 && (
        <section className="panel" key={g}>
          <h2>{t("category." + g)}<span className="count">{byCat(g).length}</span></h2>
          <div className="files">{byCat(g).map((a) => <FileCard key={a.id} a={a} task={task} onChanged={onChanged} />)}</div>
        </section>
      ))}
    </>
  );
}

function Uploader({ task, onUploaded }) {
  const [category, setCategory] = useState("before");
  const [description, setDescription] = useState("");
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef();
  const camRef = useRef();
  const toast = useToast();
  const start = async (files) => {
    for (const file of files) {
      const id = Math.random();
      const upd = (p) => setJobs((js) => js.map((j) => (j.id === id ? { ...j, ...p } : j)));
      setJobs((js) => [...js, { id, name: file.name, size: file.size, state: "preparing", sent: 0 }]);
      try {
        await uploadFile(file, { task_id: task.id, category, description: description || null }, (p) => upd(p));
        upd({ state: "done" });
        onUploaded();
      } catch (e) {
        upd({ state: "error", error: e.message, file });
        toast(`${file.name}: ${e.message}`, "error");
      }
    }
  };
  const photoOnly = category === "before" || category === "after";
  const label = { preparing: "تحضير…", uploading: "جارٍ الرفع", verifying: "التحقق على الخادم…", done: "تم الحفظ", error: "فشل" };
  return (
    <div className="uploader">
      <div className="seg">{["before", "after", "delivery", "report", "other"].map((c) => (
        <button key={c} className={"seg-btn" + (category === c ? " seg-on" : "")} onClick={() => setCategory(c)}>{t("category." + c)}</button>))}</div>
      <Field label="وصف المرفق (اختياري)"><input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <div className="actions">
        <button className="btn btn-primary" onClick={() => camRef.current.click()}>تصوير بالكاميرا</button>
        <button className="btn" onClick={() => fileRef.current.click()}>اختيار ملفات</button>
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { start([...e.target.files]); e.target.value = ""; }} />
        <input ref={fileRef} type="file" multiple hidden
               accept={photoOnly ? "image/jpeg,image/png,image/heic,image/heif,image/webp,.heic"
                 : ".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.dwg"}
               onChange={(e) => { start([...e.target.files]); e.target.value = ""; }} />
      </div>
      <p className="muted small">حتى 200 ميغابايت للملف. إذا انقطع الاتصال، اختر الملف نفسه مجددًا ليُستكمل الرفع من حيث توقف.</p>
      {jobs.length > 0 && (
        <ul className="jobs">{jobs.map((j) => (
          <li key={j.id} className={"job job-" + j.state}>
            <div className="job-name">{j.name} <small>{fmt.size(j.size)}</small></div>
            <div className="progress"><div className="progress-bar" style={{ inlineSize: (j.size ? (100 * (j.sent || 0)) / j.size : 0) + "%" }} />
              <span>{label[j.state]}{j.state === "uploading" ? ` ${Math.round((100 * j.sent) / j.size)}%` : ""}</span></div>
            {j.state === "error" && <div className="job-err">{j.error} <button className="btn btn-sm" onClick={() => {
              setJobs((js) => js.filter((x) => x.id !== j.id)); start([j.file]);
            }}>إعادة المحاولة</button></div>}
          </li>))}</ul>
      )}
    </div>
  );
}

function FileCard({ a, task, onChanged }) {
  const [thumb, setThumb] = useState(null);
  const [preview, setPreview] = useState(null);
  const { run } = useAction();
  useEffect(() => {
    let url;
    if (a.has_thumbnail) api.blobUrl(`/attachments/${a.id}/thumbnail`).then((u) => { url = u; setThumb(u); }, () => {});
    return () => url && URL.revokeObjectURL(url);
  }, [a.id, a.has_thumbnail]);
  const canPreview = a.preview === "thumbnail" || a.preview === "image" || a.preview === "pdf" || a.preview === "text";
  const openPreview = async () => setPreview({ url: await api.blobUrl(`/attachments/${a.id}/preview`), type: a.mime_type });
  const canDelete = !a.locked && (task.can.edit || task.can.add_documentation); // ownership enforced by the server
  return (
    <div className="file">
      <button className="file-thumb" onClick={canPreview ? openPreview : undefined} aria-label={"معاينة " + a.original_name} disabled={!canPreview}>
        {thumb ? <img src={thumb} alt="" /> : <span className="file-ext">{a.original_name.split(".").pop().toUpperCase()}</span>}
      </button>
      <div className="file-meta">
        <strong title={a.original_name}>{a.original_name}</strong>
        {a.description && <span>{a.description}</span>}
        <small>{a.uploaded_by_name} — {fmt.dt(a.created_at)} — {fmt.size(a.size_bytes)}{a.locked ? " — ضمن نسخة معتمدة" : ""}</small>
        <div className="actions">
          <button className="btn btn-sm" onClick={() => run(() => api.download(`/attachments/${a.id}/download`, a.original_name)).catch(() => {})}>تنزيل الأصل</button>
          {canDelete && <button className="btn btn-sm btn-ghost-danger" onClick={() => run(() => api.del(`/attachments/${a.id}`), "حُذف الملف").then(onChanged, () => {})}>حذف</button>}
        </div>
      </div>
      {preview && (
        <Modal title={a.original_name} wide onClose={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>
          {preview.type.startsWith("image/") ? <img className="preview-img" src={preview.url} alt={a.description || a.original_name} />
            : <iframe className="preview-frame" src={preview.url} title={a.original_name} sandbox="" />}
        </Modal>
      )}
    </div>
  );
}

function Reports({ task, onChanged }) {
  const draftKey = `st_draft:report:${task.id}`;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => JSON.parse(localStorage.getItem(draftKey) || "null") || { completed_work: "", test_results: "", notes: "", obstacles_note: "", quantities: [] });
  const hasDraft = !!localStorage.getItem(draftKey);
  const { busy, error, run } = useAction();
  const key = useRef(newKey());
  useEffect(() => { if (open) localStorage.setItem(draftKey, JSON.stringify(f)); }, [f, open, draftKey]);
  const save = () => run(() => api.post(`/tasks/${task.id}/reports`, {
    ...f, quantities: f.quantities.filter((q) => q.item && q.quantity !== "").map((q) => ({ ...q, quantity: Number(q.quantity) })),
  }, { key: key.current }), "حُفظ التقرير على الخادم").then(() => {
    localStorage.removeItem(draftKey);
    key.current = newKey();
    setF({ completed_work: "", test_results: "", notes: "", obstacles_note: "", quantities: [] });
    setOpen(false);
    onChanged();
  }, () => {});
  return (
    <section className="panel">
      <h2>تقارير العمل<span className="count">{task.reports.length}</span></h2>
      {task.reports.map((r) => (
        <article className="report" key={r.id}>
          <header><strong>{r.author_name}</strong><small>{fmt.dt(r.created_at)}{r.locked ? " — ضمن نسخة معتمدة" : ""}</small></header>
          <p className="prose">{r.completed_work}</p>
          {r.quantities?.length > 0 && <ul>{r.quantities.map((q, i) => <li key={i}>{q.item}: {q.quantity} {q.unit || ""}</li>)}</ul>}
          {r.test_results && <p><b>نتائج الاختبار:</b> {r.test_results}</p>}
          {r.notes && <p><b>ملاحظات:</b> {r.notes}</p>}
          {r.obstacles_note && <p><b>عوائق:</b> {r.obstacles_note}</p>}
        </article>
      ))}
      {task.can.add_documentation && !open && (
        <button className="btn" onClick={() => setOpen(true)}>{hasDraft ? "متابعة المسودة غير المرسلة" : "إضافة تقرير عمل"}</button>
      )}
      {open && (
        <div className="stack">
          <p className="callout">مسودة محفوظة على هذا الجهاز فقط — لم تُرسل بعد.</p>
          <Field label="ما تم إنجازه"><textarea rows={3} value={f.completed_work} onChange={(e) => setF({ ...f, completed_work: e.target.value })} /></Field>
          <fieldset className="field">
            <legend className="field-label">الكميات المنفذة</legend>
            {f.quantities.map((q, i) => (
              <div className="qty" key={i}>
                <input placeholder="البند" value={q.item} onChange={(e) => setF({ ...f, quantities: f.quantities.map((x, j) => (j === i ? { ...x, item: e.target.value } : x)) })} />
                <input type="number" inputMode="decimal" placeholder="الكمية" value={q.quantity} onChange={(e) => setF({ ...f, quantities: f.quantities.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)) })} />
                <input placeholder="الوحدة" value={q.unit || ""} onChange={(e) => setF({ ...f, quantities: f.quantities.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)) })} />
              </div>))}
            <button type="button" className="btn btn-sm" onClick={() => setF({ ...f, quantities: [...f.quantities, { item: "", quantity: "", unit: "" }] })}>إضافة كمية</button>
          </fieldset>
          <Field label="نتائج الاختبار"><textarea rows={2} value={f.test_results} onChange={(e) => setF({ ...f, test_results: e.target.value })} /></Field>
          <Field label="ملاحظات"><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          <Field label="العوائق"><textarea rows={2} value={f.obstacles_note} onChange={(e) => setF({ ...f, obstacles_note: e.target.value })} /></Field>
          <ErrorBox error={error} />
          <div className="actions">
            <button className="btn btn-primary" disabled={busy || f.completed_work.trim().length < 2} onClick={save}>{busy ? "جارٍ الإرسال…" : "إرسال التقرير"}</button>
            <button className="btn" onClick={() => setOpen(false)}>إخفاء (تبقى المسودة)</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- comments, reviews, activity
function Comments({ task }) {
  const [page, setPage] = useState(1);
  const state = useLoad(() => api.get(`/tasks/${task.id}/comments?page=${page}&page_size=50`), [task.id, page]);
  const [body, setBody] = useState("");
  const { busy, run } = useAction();
  const key = useRef(newKey());
  const send = () => run(() => api.post(`/tasks/${task.id}/comments`, { body }, { key: key.current }))
    .then(() => { setBody(""); key.current = newKey(); state.reload(); }, () => {});
  return (
    <div className="grid-2">
      <section className="panel">
        <h2>المراجعات</h2>
        {task.reviews.length === 0 && <p className="muted">لا توجد مراجعات بعد.</p>}
        {task.reviews.map((r) => (
          <article className={"review review-" + r.decision} key={r.id}>
            <header><strong>{r.kind === "preliminary" ? "مراجعة أولية" : "مراجعة نهائية"} — {
              { approved: "اعتماد", returned: "إعادة للتعديل", forwarded: "تحويل للاعتماد" }[r.decision]}</strong>
              <small>{r.reviewer_name} — {fmt.dt(r.created_at)} — النسخة {r.submission_seq}</small></header>
            {r.reason && <p>{r.reason}</p>}
            {r.rework_cause && <p className="muted">سبب إعادة العمل: {t("rework." + r.rework_cause)}</p>}
            {r.quality_score != null && <p>درجة الجودة: <b>{r.quality_score}</b></p>}
            {r.quality?.length > 0 && <ul className="quality">{r.quality.map((q, i) => <li key={i} className={q.passed ? "ok" : "no"}>{q.item}</li>)}</ul>}
          </article>
        ))}
      </section>
      <section className="panel">
        <h2>التعليقات</h2>
        <Loader state={state}>{(d) => (
          <>
            {d.items.length === 0 && <p className="muted">لا توجد تعليقات.</p>}
            <ul className="comments">{d.items.map((c) => (
              <li key={c.id}><strong>{c.author_name}</strong><small>{fmt.dt(c.created_at)}</small><p>{c.body}</p></li>))}</ul>
            <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} />
          </>
        )}</Loader>
        <div className="stack">
          <textarea rows={2} placeholder="اكتب تعليقًا…" value={body} onChange={(e) => setBody(e.target.value)} aria-label="تعليق" />
          <button className="btn btn-primary" disabled={busy || !body.trim()} onClick={send}>{busy ? "جارٍ الإرسال…" : "إرسال التعليق"}</button>
        </div>
      </section>
    </div>
  );
}

function Activity({ id }) {
  const [page, setPage] = useState(1);
  const state = useLoad(() => api.get(`/tasks/${id}/activity?page=${page}&page_size=50`), [id, page]);
  return (
    <section className="panel">
      <Loader state={state}>{(d) => (
        <>
          <ol className="timeline">{d.items.map((a) => (
            <li key={a.id}>
              <span className="tl-when">{fmt.dt(a.created_at)}</span>
              <span><b>{a.actor_name || "النظام"}</b> {ACTIVITY[a.type] || a.type}
                {a.data?.reason ? ` — ${a.data.reason}` : ""}{a.data?.name ? ` — ${a.data.name}` : ""}{a.data?.title ? ` — ${a.data.title}` : ""}
                {a.data?.fields ? ` (${a.data.fields.join("، ")})` : ""}</span>
            </li>))}</ol>
          <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} />
        </>
      )}</Loader>
    </section>
  );
}

// ---------------------------------------------------------------- modals
function ReviewModal({ task, preliminary, onClose, onDone }) {
  const [decision, setDecision] = useState(preliminary ? "forwarded" : "approved");
  const [quality, setQuality] = useState(QUALITY_ITEMS.map((item) => ({ item, passed: true })));
  const [reason, setReason] = useState("");
  const [cause, setCause] = useState("execution_error");
  const { busy, error, run } = useAction();
  const score = Math.round((quality.filter((q) => q.passed).length * 100) / quality.length);
  const submit = () => run(() => api.post(`/tasks/${task.id}/review`, {
    kind: preliminary ? "preliminary" : "final", decision,
    reason: reason || null, rework_cause: decision === "returned" ? cause : null,
    quality: decision === "approved" ? quality : [],
  }), decision === "approved" ? "تم اعتماد المهمة" : decision === "returned" ? "أعيدت المهمة للموظف" : "حُوّلت للاعتماد").then(onDone, () => {});
  return (
    <Modal title={preliminary ? "مراجعة أولية" : "مراجعة واعتماد"} onClose={onClose} wide>
      <div className="seg">
        {(preliminary ? [["forwarded", "سليمة — تحويل للاعتماد"], ["returned", "إعادة للتعديل"]]
          : [["approved", "اعتماد"], ["returned", "إعادة للتعديل"]]).map(([k, l]) => (
          <button key={k} className={"seg-btn" + (decision === k ? " seg-on" : "")} onClick={() => setDecision(k)}>{l}</button>))}
      </div>
      {decision === "approved" && (
        <fieldset className="field">
          <legend className="field-label">قائمة مراجعة الجودة — الدرجة {score}</legend>
          <ul className="checklist">{quality.map((q, i) => (
            <li key={q.item} className={q.passed ? "done" : ""}><label>
              <input type="checkbox" checked={q.passed} onChange={() => setQuality((xs) => xs.map((x, j) => (j === i ? { ...x, passed: !x.passed } : x)))} />
              <span>{q.item}</span></label></li>))}</ul>
        </fieldset>
      )}
      {decision === "returned" && (
        <Field label="سبب إعادة العمل">
          <div className="seg">{Object.keys(t("rework")).map((k) => (
            <button key={k} className={"seg-btn" + (cause === k ? " seg-on" : "")} onClick={() => setCause(k)}>{t("rework." + k)}</button>))}</div>
        </Field>
      )}
      <Field label={decision === "returned" ? "ما المطلوب تعديله؟ (إلزامي — يصل للموظف)" : "ملاحظة (اختياري)"}>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <p className="muted small">خطأ التنفيذ يؤثر على درجة الجودة في التقييم، وتغيير النطاق لا يؤثر، ونقص التوثيق يؤثر على مكوّن التوثيق.</p>
      <ErrorBox error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy || (decision === "returned" && !reason.trim())} onClick={submit}>{busy ? "جارٍ الحفظ…" : "تأكيد"}</button>
        <button className="btn" onClick={onClose}>{t("cancel")}</button>
      </div>
    </Modal>
  );
}

function ObstacleModal({ task, onClose, onDone }) {
  const [f, setF] = useState({ reason_code: "materials_shortage", description: "", occurred_at: toLocalInput(new Date().toISOString()), blocking: "full" });
  const [files, setFiles] = useState([]);
  const { busy, error, run } = useAction();
  const key = useRef(newKey());
  const submit = () => run(async () => {
    const o = await api.post(`/tasks/${task.id}/obstacles`, { ...f, occurred_at: fromLocalInput(f.occurred_at) }, { key: key.current });
    for (const file of files) await uploadFile(file, { obstacle_id: o.id, category: "obstacle_evidence" });
    return o;
  }, "سُجّل العائق وأُبلغ مدير المشروع").then(onDone, () => {});
  return (
    <Modal title="تسجيل عائق" onClose={onClose}>
      <Field label="السبب">
        <select value={f.reason_code} onChange={(e) => setF({ ...f, reason_code: e.target.value })}>
          {Object.keys(t("reason")).map((k) => <option key={k} value={k}>{t("reason." + k)}</option>)}
        </select>
      </Field>
      <Field label="الوصف"><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Field label="وقت الحدوث"><input type="datetime-local" value={f.occurred_at} onChange={(e) => setF({ ...f, occurred_at: e.target.value })} /></Field>
      <div className="seg">
        <button className={"seg-btn" + (f.blocking === "full" ? " seg-on" : "")} onClick={() => setF({ ...f, blocking: "full" })}>يمنع التنفيذ كليًا</button>
        <button className={"seg-btn" + (f.blocking === "partial" ? " seg-on" : "")} onClick={() => setF({ ...f, blocking: "partial" })}>يعيقه جزئيًا</button>
      </div>
      <Field label="أدلة (صور أو ملفات)"><input type="file" multiple accept="image/*,.pdf" onChange={(e) => setFiles([...e.target.files])} /></Field>
      <p className="muted small">لا تُستبعد مدة التأخير من التقييم إلا بعد اعتماد المدير للعائق.</p>
      <ErrorBox error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy || f.description.trim().length < 3} onClick={submit}>{busy ? "جارٍ الإرسال…" : "إرسال الطلب"}</button>
        <button className="btn" onClick={onClose}>{t("cancel")}</button>
      </div>
    </Modal>
  );
}

function ContributorsModal({ task, onClose, onDone }) {
  const people = useLoad(() => api.get("/users" + qs({ status: "active", page_size: 100, team_id: task.team_id })), []);
  const [rows, setRows] = useState(task.contributors.map((c) => ({ user_id: c.user_id, share_pct: Number(c.share_pct) })));
  const [mainShare, setMainShare] = useState(Number(task.assignee_share_pct));
  const [reason, setReason] = useState("");
  const { busy, error, run } = useAction();
  const total = mainShare + rows.reduce((s, r) => s + Number(r.share_pct || 0), 0);
  const sharesUsed = mainShare !== 100 || rows.some((r) => Number(r.share_pct) > 0);
  return (
    <Modal title="المساهمون وحصص الإنجاز" onClose={onClose}>
      <p className="muted small">لا يُنسب كامل وزن المهمة لأكثر من شخص. إن وُزعت الحصص فيجب أن يكون مجموعها 100%.</p>
      <Field label={`حصة المسؤول (${task.assignee_name}) %`}>
        <input type="number" min={0} max={100} value={mainShare} onChange={(e) => setMainShare(Number(e.target.value))} />
      </Field>
      {rows.map((r, i) => (
        <div className="qty" key={i}>
          <select value={r.user_id} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, user_id: e.target.value } : x)))}>
            <option value="">اختر</option>
            {(people.data?.items || []).filter((u) => u.id !== task.assignee_id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input type="number" min={0} max={100} value={r.share_pct} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, share_pct: Number(e.target.value) } : x)))} />
          <button className="icon-btn" aria-label="إزالة" onClick={() => setRows(rows.filter((_, j) => j !== i))}>✕</button>
        </div>))}
      <button className="btn btn-sm" onClick={() => setRows([...rows, { user_id: "", share_pct: 0 }])}>إضافة مساعد</button>
      <p className={sharesUsed && total !== 100 ? "late" : "muted"}>المجموع: {total}%</p>
      {task.locked_at && sharesUsed && <Field label="سبب التعديل (إلزامي بعد بدء التنفيذ)"><textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>}
      <ErrorBox error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy || (sharesUsed && total !== 100) || rows.some((r) => !r.user_id)}
                onClick={() => run(() => api.put(`/tasks/${task.id}/contributors`, { contributors: rows, assignee_share_pct: mainShare, reason: reason || null }), "تم حفظ المساهمين").then(onDone, () => {})}>
          حفظ</button>
        <button className="btn" onClick={onClose}>{t("cancel")}</button>
      </div>
    </Modal>
  );
}

export { ACTIVITY };
